import { memo, useMemo } from 'react';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';

// Regex patterns for LaTeX detection
const INLINE_LATEX = /\$([^$]+)\$/g;
const BLOCK_LATEX = /\$\$([^$]+)\$\$/g;

const LatexText = memo(({ text, className = '' }) => {
  const rendered = useMemo(() => {
    if (!text) return null;
    
    // Check if text contains any LaTeX
    const hasBlockLatex = text.includes('$$');
    const hasInlineLatex = /\$[^$]+\$/.test(text);
    
    if (!hasBlockLatex && !hasInlineLatex) {
      return <span className={className}>{text}</span>;
    }

    const parts = [];
    let lastIndex = 0;
    let key = 0;

    // First handle block LaTeX ($$...$$)
    let processedText = text;
    const blockMatches = [...text.matchAll(/\$\$([^$]+)\$\$/g)];
    
    if (blockMatches.length > 0) {
      blockMatches.forEach(match => {
        const beforeText = processedText.substring(lastIndex, match.index);
        if (beforeText) {
          // Process inline LaTeX in the before text
          parts.push(...parseInlineLatex(beforeText, key));
          key += 100;
        }
        
        try {
          parts.push(
            <BlockMath key={`block-${key++}`} math={match[1].trim()} />
          );
        } catch (e) {
          parts.push(<span key={`error-${key++}`} className="text-red-500">{match[0]}</span>);
        }
        lastIndex = match.index + match[0].length;
      });
      
      // Handle remaining text after last block match
      const remaining = processedText.substring(lastIndex);
      if (remaining) {
        parts.push(...parseInlineLatex(remaining, key));
      }
      
      return <span className={className}>{parts}</span>;
    }

    // Only inline LaTeX
    return <span className={className}>{parseInlineLatex(text, 0)}</span>;
  }, [text, className]);

  return rendered;
});

// Helper function to parse inline LaTeX
function parseInlineLatex(text, startKey) {
  const parts = [];
  let lastIndex = 0;
  let key = startKey;
  
  const matches = [...text.matchAll(/\$([^$]+)\$/g)];
  
  if (matches.length === 0) {
    return [<span key={key}>{text}</span>];
  }
  
  matches.forEach(match => {
    const beforeText = text.substring(lastIndex, match.index);
    if (beforeText) {
      parts.push(<span key={`text-${key++}`}>{beforeText}</span>);
    }
    
    try {
      parts.push(
        <InlineMath key={`inline-${key++}`} math={match[1].trim()} />
      );
    } catch (e) {
      parts.push(<span key={`error-${key++}`} className="text-red-500">{match[0]}</span>);
    }
    lastIndex = match.index + match[0].length;
  });
  
  // Handle remaining text
  const remaining = text.substring(lastIndex);
  if (remaining) {
    parts.push(<span key={`text-${key++}`}>{remaining}</span>);
  }
  
  return parts;
}

LatexText.displayName = 'LatexText';
export default LatexText;
